import { getServiceTypeString } from '../../appointment/appointmentFunctions';
import { isStaffUser } from '../../common';
import { EditIcon } from '../../buttons/EditIcon.tsx';
import AddUsersToChat from './AddUsersToChat.js';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export default function ChatHeader({
    selectedAppointment,
    serviceTypes,
    loggedInUser,
    handleMouseDown,
    onViewAppointmentClick,
    matrixClient,
    practiceId,
    token
}) {
    const dispatch = useDispatch();
    const chatDetails = useSelector(state => state.chatDetails);
    const [showAddUsers, setShowAddUsers] = useState(false);

    const synapseRoomId = chatDetails.synapseRoomId;

    const serviceLocName = getServiceLocationName(selectedAppointment);
    const patientName = getPatientName(selectedAppointment);


    function getServiceLocationName(selectedAppointment) {

        if (selectedAppointment && selectedAppointment.nearest_practices) {

            // Find the service location that matches the current synapseRoomId from chatDetails
            const currentPractice = selectedAppointment.nearest_practices.find(practice =>
                practice.synapse_room_id === synapseRoomId
            );

            if (currentPractice) {
                return currentPractice.location_name;
            } else {
                // Fallback: if no exact match found, take the first one
                return selectedAppointment.nearest_practices[0]?.location_name || 'Unknown Location';
            }
        }
        return '';
    }

    function getPatientName(selectedAppointment) {
        console.log('=== CHAT HEADER PATIENT NAME DEBUG ===');
        console.log('ChatHeader getPatientName - selectedAppointment:', selectedAppointment);
        console.log('selectedAppointment keys:', selectedAppointment ? Object.keys(selectedAppointment) : 'null');
        
        if (selectedAppointment && selectedAppointment.created_by_user) {
            console.log('ChatHeader getPatientName - created_by_user:', selectedAppointment.created_by_user);
            const firstName = selectedAppointment.created_by_user.firstname || '';
            const lastName = selectedAppointment.created_by_user.lastname || '';
            const username = selectedAppointment.created_by_user.username || '';
            const email = selectedAppointment.created_by_user.email || '';
            
            console.log('Name fields - firstname:', firstName, 'lastname:', lastName, 'username:', username, 'email:', email);
            
            if (firstName && lastName) {
                return `${firstName} ${lastName}`;
            } else if (firstName) {
                return firstName;
            } else if (lastName) {
                return lastName;
            } else if (username) {
                // Fallback: use username if name fields are empty
                return username;
            } else if (email) {
                // Fallback: use email part before @ if username is also empty
                return email.split('@')[0];
            } else {
                // Final fallback: use user ID
                return `User ${selectedAppointment.created_by_user._id?.slice(-6) || 'Unknown'}`;
            }
        }
        
        // For patient users, try to get name from logged-in user if created_by_user is not available
        if (selectedAppointment && loggedInUser) {
            console.log('ChatHeader getPatientName - using loggedInUser:', loggedInUser);
            const firstName = loggedInUser.firstname || '';
            const lastName = loggedInUser.lastname || '';
            if (firstName && lastName) {
                return `${firstName} ${lastName}`;
            } else if (firstName) {
                return firstName;
            } else if (lastName) {
                return lastName;
            } else {
                return loggedInUser.username || '';
            }
        }
        
        console.log('ChatHeader getPatientName - no name found');
        return '';
    }

    function getAppointmentStatus(selectedAppointment) {
        console.log('ChatHeader getAppointmentStatus - selectedAppointment:', selectedAppointment);
        console.log('ChatHeader getAppointmentStatus - synapseRoomId:', synapseRoomId);
        
        if (selectedAppointment && selectedAppointment.nearest_practices) {
            // Find the practice that matches the current synapseRoomId
            const currentPractice = selectedAppointment.nearest_practices.find(practice =>
                practice.synapse_room_id === synapseRoomId
            );
            
            if (currentPractice) {
                const status = currentPractice.practice_appointment_status || 'Unknown';
                console.log('ChatHeader getAppointmentStatus - found matching practice, status:', status);
                return status;
            } else {
                // Fallback: if no exact match found, take the first one
                const status = selectedAppointment.nearest_practices[0].practice_appointment_status || 'Unknown';
                console.log('ChatHeader getAppointmentStatus - no matching practice, using first practice status:', status);
                return status;
            }
        }
        
        // Try to get status from appointment directly if nearest_practices is not available
        if (selectedAppointment && selectedAppointment.patient_appointment_status) {
            console.log('ChatHeader getAppointmentStatus - using patient_appointment_status:', selectedAppointment.patient_appointment_status);
            return selectedAppointment.patient_appointment_status;
        }
        
        console.log('ChatHeader getAppointmentStatus - no status found');
        return 'Unknown';
    }

    function getServiceType(selectedAppointment) {
        console.log('ChatHeader getServiceType - selectedAppointment:', selectedAppointment);
        console.log('ChatHeader getServiceType - serviceTypes:', serviceTypes);
        
        if (selectedAppointment) {
            // For patients, service_type is directly on the appointment object
            const serviceTypeCode = selectedAppointment.service_type || selectedAppointment.appointment_basic_details?.service_type;
            console.log('ChatHeader getServiceType - serviceTypeCode:', serviceTypeCode);
            
            if (serviceTypeCode) {
                const serviceType = getServiceTypeString(serviceTypeCode, serviceTypes);
                console.log('ChatHeader getServiceType - found serviceType:', serviceType);
                return serviceType;
            }
        }
        
        console.log('ChatHeader getServiceType - no service type found');
        return 'Unknown Service';
    }

    function getStatusColor(status) {
        switch (status) {
            case 'NEW':
                return 'bg-blue-100 text-blue-700';
            case 'SUSPENDED':
                return 'bg-yellow-100 text-yellow-700';
            case 'ACTIVE':
                return 'bg-green-100 text-green-700';
            case 'CLOSED':
            case 'DELETED':
            case 'DECLINED':
                return 'bg-red-100 text-red-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    }


    const appointmentStatus = getAppointmentStatus(selectedAppointment);
    const serviceType = getServiceType(selectedAppointment);


    return (
        <>
            <div className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
                {/* Draggable header area */}
                <div
                    className="flex items-center justify-between px-6 py-4 cursor-move"
                    onMouseDown={handleMouseDown}
                >
                    {/* Left section - Title and subtitle */}
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                        <div className="flex flex-col min-w-0">
                            <h2 className="text-gray-900 truncate font-semibold text-lg leading-tight">
                                {isStaffUser(loggedInUser) ? patientName : serviceLocName}
                            </h2>
                            <span className="text-gray-500 mt-1 font-medium text-sm leading-tight">
                                {serviceType}
                            </span>
                        </div>
                    </div>

                    {/* Right section - Status and actions */}
                    <div className="flex flex-col items-end space-y-1 flex-shrink-0">
                        {/* First row - Status only */}
                        <div className="flex items-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(appointmentStatus)} shadow-sm`}>
                                {appointmentStatus}
                            </span>
                        </div>
                        
                        {/* Second row - Edit button aligned with serviceType */}
                        <div className="flex items-center">
                            <button
                                onClick={onViewAppointmentClick}
                                className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1"
                                title="View Appointment Details"
                            >
                                <EditIcon className='w-6 h-6 text-gray-600 transform -rotate-90' />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <AddUsersToChat
                show={showAddUsers}
                onClose={() => setShowAddUsers(false)}
                roomId={synapseRoomId}
                matrixClient={matrixClient}
                loggedInUser={loggedInUser}
                practiceId={practiceId}
                token={token}
                dispatch={dispatch}
            />
        </>
    );
}
